import { isEmpty } from "lodash-es";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { DetailSection } from "@/components/Section";
import { IWorkOrder } from "@/data/work-orders";
import { Flex, IFlexProps } from "@/eworkitComponents";
import { HtmlRenderer } from "@/eworkitMove/HtmlRenderer";
import { CustomField, CustomFields } from "@/views/components/CustomFields";
import { JobRequestLanguages, JobRequestSkills } from "@/views/components/JobRequests/Partials";

interface IWorkOrderSpecificationProps extends Partial<IFlexProps> {
  workOrder: IWorkOrder;
}

export const WorkOrderSpecification: FC<IWorkOrderSpecificationProps> = ({ workOrder, ...props }) => {
  const { t } = useTranslation("session");
  const [requiredSkills, preferredSkills] = workOrder.skills.reduce(
    ([reqSkills, prefSkills], skill) =>
      skill.priority === "REQUIRED" ? [[...reqSkills, skill], prefSkills] : [reqSkills, [...prefSkills, skill]],
    [[], []],
  );

  const referenceIdEmpty = isEmpty(workOrder.jobRequestDetails?.businessReferenceId);

  const baseClass = "work-order-specification";

  return (
    <Flex innerMargin="md" direction="column" {...props}>
      <DetailSection
        className={`${baseClass}__section`}
        isVisible={!isEmpty(workOrder.agreementDescription)}
        label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_DESCRIPTION")}
        weight="bold">
        <HtmlRenderer html={workOrder.agreementDescription} />
      </DetailSection>

      <DetailSection
        className={`${baseClass}__section`}
        isVisible={!isEmpty(workOrder.levelDescription)}
        label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_LEVEL_DESCRIPTION")}
        weight="bold">
        <HtmlRenderer html={workOrder.levelDescription} />
      </DetailSection>

      <DetailSection
        className={`${baseClass}__section`}
        isVisible={!isEmpty(workOrder.roleDescription)}
        label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_ROLE_DESCRIPTION")}
        weight="bold">
        <HtmlRenderer html={workOrder.roleDescription} />
      </DetailSection>

      <DetailSection
        className={`${baseClass}__section`}
        isVisible={!isEmpty(workOrder.qualificationCriteria)}
        label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_SKILL_REQUIREMENTS_DESCRIPTION")}
        weight="bold">
        <HtmlRenderer html={workOrder.qualificationCriteria} />
      </DetailSection>

      <DetailSection
        isVisible={!isEmpty(requiredSkills)}
        label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_REQUIRED_SKILLS")}
        weight="bold">
        <JobRequestSkills skills={requiredSkills} showMore={true} visibleSkillsCount={15} />
      </DetailSection>

      <DetailSection
        className={`${baseClass}__section`}
        isVisible={!isEmpty(preferredSkills)}
        label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_PREFERRED_SKILLS")}
        weight="bold">
        <JobRequestSkills skills={preferredSkills} showMore={true} visibleSkillsCount={15} />
      </DetailSection>

      <DetailSection
        className={`${baseClass}__section`}
        isVisible={!isEmpty(workOrder.languages)}
        label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_LANGUAGES")}
        weight="bold">
        <JobRequestLanguages languages={workOrder.languages} />
      </DetailSection>

      <DetailSection
        className={`${baseClass}__section`}
        isVisible={!(isEmpty(workOrder.customFields) && referenceIdEmpty)}
        label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_CUSTOM_FIELDS")}
        weight="bold">
        <CustomFields customFields={workOrder.customFields}>
          {!referenceIdEmpty && (
            <CustomField
              customField={{
                name: t("JOB_REQUESTS.DETAIL.REFERENCE_ID"),
                value: workOrder.jobRequestDetails.businessReferenceId!,
                type: "TEXT",
                order: 0,
              }}
            />
          )}
        </CustomFields>
      </DetailSection>
    </Flex>
  );
};
